import { useState, useEffect } from "react";
// import '../../assets/css/navbar.css';
import '../../App.css';
import { useNavigate, useLocation } from "react-router-dom";
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../../assets/images/logo.png";

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Desktop layout is active for 1024px and above; mobile drawer is active below 1024px
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const menuItems = [
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: "About Us", path: "/about" },
        { name: "Legal & Policies", path: "/legal_and_polices" },
        { name: "Contact Us", path: "/contact" },
    ];

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuItemClick = () => {
        setMobileOpen(false);
    };

    const drawer = (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#0A0A0A",
                color: "#fff",
                padding: { xs: 2, sm: 3 },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 4,
                }}
            >
                <IconButton id="drawer-close-btn" onClick={handleDrawerToggle} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <List>
                {menuItems.map((item, index) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem
                            key={item.name}
                            id={`drawer-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => {
                                handleMenuItemClick();
                                if (item.path.startsWith("/")) {
                                    navigate(item.path);
                                }
                            }}
                            sx={{
                                py: 1.5,
                                borderBottom: index === menuItems.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)",
                                borderLeft: isActive ? "3px solid var(--color-primary)" : "3px solid transparent",
                                pl: isActive ? 1.5 : 2,
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    backgroundColor: "rgba(122, 212, 197, 0.08)",
                                },
                            }}
                        >
                            <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                    className: `navbar-link ${isActive ? 'navbar-link-active' : 'navbar-link-inactive'}`,
                                    sx: {
                                        fontSize: { xs: "15px", sm: "16px" },
                                        color: isActive ? 'var(--color-primary)' : 'rgba(255,255,255,0.75)',
                                        fontWeight: isActive ? 700 : 400,
                                    },
                                }}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: scrolled
                        ? "rgba(0, 0, 0, 0.8)"
                        : "rgba(0, 0, 0, 0.4)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.1)" : "none",
                    transition: "all 0.3s ease",
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar
                        disableGutters
                        sx={{
                            minHeight: { xs: "70px", sm: "80px" },
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        {/* Logo */}
                        <Box
                            component="img"
                            src={logo}
                            alt="Payible"
                            id="navbar-logo"
                            onClick={() => navigate('/')}
                            sx={{
                                height: { xs: 28, sm: 32, md: 40 },
                                cursor: "pointer",
                            }}
                        />

                        {/* Desktop Menu */}
                        <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: { md: 2, lg: 4, xl: 5 } }}>
                            {menuItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <Button
                                        key={item.name}
                                        id={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        className={`navbar-link ${isActive ? 'navbar-link-active' : 'navbar-link-inactive'}`}
                                        onClick={() => {
                                            if (item.path.startsWith("/")) {
                                                navigate(item.path);
                                            }
                                        }}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: { xs: "14px", lg: "15px", xl: "16px" },
                                            position: "relative",
                                            fontWeight: 600,
                                            padding: "8px 16px",
                                            "&::after": isActive
                                                ? {
                                                    content: '""',
                                                    position: "absolute",
                                                    bottom: 0,
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    width: "80%",
                                                    height: "2px",
                                                    bgcolor: "var(--color-primary)",
                                                }
                                                : {},
                                        }}
                                    >
                                        {item.name}
                                    </Button>
                                );
                            })}
                        </Box>

                        {/* Mobile Menu Button */}
                        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                            <IconButton
                                id="nav-drawer-toggle"
                                edge="end"
                                onClick={handleDrawerToggle}
                                sx={{
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "rgba(89, 245, 225, 0.1)",
                                    },
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Spacer to prevent content from hiding under fixed navbar */}
            <Toolbar sx={{ minHeight: { xs: "70px", sm: "80px" } }} />

            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                disableScrollLock
                sx={{
                    display: 'block',

                    "& .MuiDrawer-paper": {
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        width: "60%",
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Navbar;